import { Loading, notify, TableCard, TableOutput } from "@general-backoffice/core";
import type { Invoice, ManualPaymentHistoryItem, PaginationMeta } from "@investhinks/api-client";
import { saveAs } from "file-saver";
import moment from "moment";
import React from "react";
import { useTranslation } from "react-i18next";
import { Badge, Button, Card } from "reactstrap";

import { manualPaymentsEndpoints } from "../../api";
import { notifyLoadingToError, notifyLoadingToSuccess } from "../../utils/notify/notifyLoadingUpdate";
import { TypedTable } from "../../utils/typedLib";
import { getCurrencyDisplay } from "../utils/currency";
import { getStatusColor } from "../utils/statusColor";

type TableInvoice = Invoice | ManualPaymentHistoryItem;

function isInvoice(item: TableInvoice): item is Invoice {
  // Invoice extends StrapiEntitySchema which includes createdAt; ManualPaymentHistoryItem does not
  return "createdAt" in item;
}

interface ManualPaymentInvoicesTableProps {
  title?: string;
  invoices?: TableInvoice[];
  isLoading: boolean;
  pagination?: PaginationMeta;
  goToPage: (page: number) => void;
  changePageSize: (size: number) => void;
  onRecordPayment?: (invoice: Invoice) => void;
  showHistory?: boolean;
}

const statusColor: Record<string, string> = {
  pending: "warning",
  processing: "info",
  paid: "success",
  failed: "danger",
  cancelled: "secondary",
};

const ManualPaymentInvoicesTable: React.FC<ManualPaymentInvoicesTableProps> = ({
  title,
  invoices,
  isLoading,
  pagination,
  goToPage,
  changePageSize,
  onRecordPayment,
  showHistory = false,
}) => {
  const { t } = useTranslation("manualPayments", { keyPrefix: "table" });

  const downloadInvoicePDF = async (invoice: TableInvoice) => {
    const loadingNotification = notify.loading(t("downloading"));
    try {
      const blob = await manualPaymentsEndpoints.downloadInvoicePDF(Number(invoice.id));
      saveAs(blob, `invoice_${invoice.invoiceNumber}.pdf`);
      notifyLoadingToSuccess(loadingNotification, t("downloaded"));
    } catch {
      notifyLoadingToError(loadingNotification, t("downloadError"));
    }
  };

  const columns = [
    {
      name: t("columns.user"),
      formattedContent: ({ user }: TableInvoice) => (
        <>
          <div>{user?.name || user?.email}</div>
          {user?.name && <small className="text-muted">{user?.email}</small>}
        </>
      ),
      isTh: true,
    },
    {
      name: t("columns.invoiceNumber"),
      formattedContent: ({ invoiceNumber }: TableInvoice) => <span className="font-weight-bold">{invoiceNumber}</span>,
    },
    {
      name: t("columns.period"),
      formattedContent: (inv: TableInvoice) => {
        const description = "description" in inv ? inv.description : undefined;
        const date = "createdAt" in inv ? inv.createdAt : inv.invoiceDate;
        return (
          <div>
            <div>{description || "-"}</div>
            <small className="text-muted">{date ? moment(date).format("DD/MM/YYYY") : "-"}</small>
          </div>
        );
      },
    },
    {
      name: t("columns.amount"),
      formattedContent: ({ netPayable, currency }: TableInvoice) => (
        <span className="font-weight-bold">
          {netPayable?.toFixed(2)} {getCurrencyDisplay(currency)}
        </span>
      ),
    },
    {
      name: t("columns.dueDate"),
      formattedContent: (inv: TableInvoice) => {
        const dueDate = "dueDate" in inv ? inv.dueDate : undefined;
        if (!dueDate) return "-";
        const due = moment(dueDate);
        const now = moment();
        const isOverdue = due.isBefore(now, "day");
        const daysUntil = due.diff(now, "days");

        return (
          <div>
            <div className={isOverdue ? "text-danger" : ""}>{due.format("DD/MM/YYYY")}</div>
            {isOverdue ? (
              <small className="text-danger">{t("overdue", { days: Math.abs(daysUntil) })}</small>
            ) : daysUntil <= 7 ? (
              <small className="text-warning">{t("dueIn", { days: daysUntil })}</small>
            ) : null}
          </div>
        );
      },
    },
    {
      name: t("columns.status"),
      formattedContent: ({ status, manualPaymentRecordedAt }: TableInvoice) => (
        <div>
          <TableOutput.Status
            status={t(`statuses.${status}`, status || "")}
            color={getStatusColor(statusColor, status)}
          />
          {status === "paid" && manualPaymentRecordedAt && (
            <div>
              <small className="text-muted">{moment(manualPaymentRecordedAt).format("DD/MM/YYYY")}</small>
            </div>
          )}
        </div>
      ),
    },
    {
      name: t("columns.paymentMethod"),
      hidden: !showHistory,
      formattedContent: ({ manualPaymentDetails }: TableInvoice) => {
        if (!manualPaymentDetails) return "-";
        const method = manualPaymentDetails.paymentMethod;
        return (
          <Badge color={method === "crypto" ? "primary" : "info"}>
            {method === "crypto" ? t("methods.crypto") : t("methods.bankTransfer")}
          </Badge>
        );
      },
    },
    {
      name: "",
      formattedContent: (invoice: TableInvoice) => (
        <div className="d-flex" style={{ gap: "0.5rem" }}>
          <Button
            size="sm"
            color="primary"
            outline
            onClick={(e: React.MouseEvent) => {
              e.stopPropagation();
              downloadInvoicePDF(invoice);
            }}
            title={t("downloadPDF")}
          >
            <i className="fas fa-download" />
          </Button>
          {!showHistory && invoice.status === "pending" && (
            <Button
              size="sm"
              color="success"
              onClick={(e: React.MouseEvent) => {
                e.stopPropagation();
                if (isInvoice(invoice)) {
                  onRecordPayment?.(invoice);
                }
              }}
              title={t("recordPayment")}
            >
              <i className="fas fa-check mr-1" />
              {t("recordPayment")}
            </Button>
          )}
        </div>
      ),
    },
  ];

  return (
    <Card>
      <TableCard.Header title={title || t("title")} subtitle={t("subtitle")} isLoading={isLoading} />
      {isLoading && <Loading.Line />}

      <TypedTable
        columns={columns}
        rows={invoices || []}
        isLoading={isLoading}
        noContentMessage={t("noContent")}
        rowHeight={70}
        rowAmount={pagination?.pageSize}
        selectable={false}
        sortable={false}
      />
      <TableCard.Footer
        amount={pagination?.pageSize ?? 10}
        setAmount={changePageSize}
        totalEntries={pagination?.total ?? 0}
        page={(pagination?.page ?? 1) - 1}
        onChangePage={(page: number) => goToPage(page + 1)}
        isLoading={isLoading}
        amountOptions={[5, 10, 20, 50]}
      />
    </Card>
  );
};

export default ManualPaymentInvoicesTable;
